import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  UserX,
  UserCheck,
  Search,
  Package,
  Save,
  Trash2,
  Key,
  Eye,
  EyeOff,
  DollarSign,
  Truck,
  ShoppingCart,
  Smartphone,
  Link2,
  Copy,
  Check,
  ExternalLink,
  Store,
  RefreshCw,
  Receipt,
} from "lucide-react";

export default function AdminVendedores() {
  const [vendedores, setVendedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inventarioDialogOpen, setInventarioDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedVendedor, setSelectedVendedor] = useState(null);
  const [inventarioVendedor, setInventarioVendedor] = useState([]);
  const [editingInventario, setEditingInventario] = useState({});
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [vendedorConfig, setVendedorConfig] = useState({
    apoyo_transporte: 0,
    tipo_auxilio_transporte: "periodico", // "diario" o "periodico"
    otros_costos: 0,
    notas_config: "",
    puede_registrar_gastos: false,
    can_deliver_orders: false,
  });
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogCode, setCatalogCode] = useState("");
  const [catalogCopied, setCatalogCopied] = useState(false);
  const [generatingCatalog, setGeneratingCatalog] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    nombre: "",
    telefono: "",
    zona: "",
    rol: "vendedor",
    tipo_vendedor: "carrito",
    meta_diaria: "150000",
  });

  useEffect(() => {
    fetchVendedores();
  }, []);

  const fetchVendedores = async () => {
    try {
      const response = await api.get("/usuarios");
      setVendedores(response.data);
    } catch (error) {
      toast.error("Error al cargar vendedores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/usuarios/${editingId}`, {
          email: form.email,
          nombre: form.nombre,
          telefono: form.telefono,
          zona: form.zona,
          rol: form.rol,
          tipo_vendedor: form.tipo_vendedor,
          meta_diaria: parseFloat(form.meta_diaria),
        });
        toast.success("Vendedor actualizado");
      } else {
        await api.post("/usuarios", {
          ...form,
          meta_diaria: parseFloat(form.meta_diaria),
        });
        toast.success("Vendedor creado");
      }
      setDialogOpen(false);
      resetForm();
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (vendedor) => {
    setEditingId(vendedor.id);
    setForm({
      email: vendedor.email,
      password: "",
      nombre: vendedor.nombre,
      telefono: vendedor.telefono || "",
      zona: vendedor.zona || "",
      rol: vendedor.rol,
      tipo_vendedor: vendedor.tipo_vendedor || "carrito",
      meta_diaria: vendedor.meta_diaria?.toString() || "150000",
    });
    setDialogOpen(true);
  };

  const handleToggle = async (id) => {
    try {
      await api.delete(`/usuarios/${id}`);
      toast.success("Estado actualizado");
      fetchVendedores();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const handleDelete = async () => {
    if (!selectedVendedor) return;
    try {
      await api.delete(`/usuarios/${selectedVendedor.id}/permanente`);
      toast.success("Vendedor eliminado permanentemente");
      setDeleteDialogOpen(false);
      setSelectedVendedor(null);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  const handleChangePassword = async () => {
    if (!selectedVendedor || !newPassword) return;
    try {
      await api.put(`/usuarios/${selectedVendedor.id}/password`, {
        password: newPassword,
      });
      toast.success("Contraseña actualizada");
      setPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedVendedor(null);
    } catch (error) {
      toast.error("Error al cambiar contraseña");
    }
  };

  const handleVerInventario = async (vendedor) => {
    setSelectedVendedor(vendedor);
    try {
      // Get current inventory for this vendor
      const inventarioRes = await api.get(`/inventario/vendedor/${vendedor.id}`);
      const inventarioActual = inventarioRes.data;
      
      // Get all active products
      const productosRes = await api.get("/productos");
      const todosProductos = productosRes.data.filter(p => p.activo);
      
      // Create a map of existing inventory
      const inventarioMap = {};
      inventarioActual.forEach(inv => {
        inventarioMap[inv.producto_nombre] = inv;
      });
      
      // Combine: existing inventory + products without inventory
      const inventarioCompleto = todosProductos.map(prod => {
        if (inventarioMap[prod.nombre]) {
          return inventarioMap[prod.nombre];
        } else {
          // Create a placeholder for products without inventory
          return {
            id: `new_${prod.id}`,
            producto_id: prod.id,
            producto_nombre: prod.nombre,
            cantidad_asignada: 0,
            cantidad_disponible: 0,
            isNew: true
          };
        }
      });
      
      setInventarioVendedor(inventarioCompleto);
      const editState = {};
      inventarioCompleto.forEach((inv) => {
        editState[inv.id] = inv.cantidad_disponible;
      });
      setEditingInventario(editState);
      setInventarioDialogOpen(true);
    } catch (error) {
      toast.error("Error al cargar inventario");
    }
  };

  const handleUpdateInventario = async (inventarioItem) => {
    const inventarioId = inventarioItem.id;
    const cantidad = parseInt(editingInventario[inventarioId]) || 0;
    
    try {
      if (inventarioItem.isNew) {
        // Create new inventory record
        await api.post("/inventario", {
          vendedor_id: selectedVendedor.id,
          producto_id: inventarioItem.producto_id,
          producto_nombre: inventarioItem.producto_nombre,
          cantidad_asignada: cantidad,
          cantidad_disponible: cantidad
        });
        toast.success(`Inventario de ${inventarioItem.producto_nombre} creado`);
      } else {
        // Update existing inventory
        await api.put(`/inventario/${inventarioId}`, {
          cantidad_disponible: cantidad,
        });
        toast.success("Inventario actualizado");
      }
      
      // Reload inventory
      const response = await api.get(`/inventario/vendedor/${selectedVendedor.id}`);
      const productosRes = await api.get("/productos");
      const todosProductos = productosRes.data.filter(p => p.activo);
      
      const inventarioMap = {};
      response.data.forEach(inv => {
        inventarioMap[inv.producto_nombre] = inv;
      });
      
      const inventarioCompleto = todosProductos.map(prod => {
        if (inventarioMap[prod.nombre]) {
          return inventarioMap[prod.nombre];
        } else {
          return {
            id: `new_${prod.id}`,
            producto_id: prod.id,
            producto_nombre: prod.nombre,
            cantidad_asignada: 0,
            cantidad_disponible: 0,
            isNew: true
          };
        }
      });
      
      setInventarioVendedor(inventarioCompleto);
      const editState = {};
      inventarioCompleto.forEach((inv) => {
        editState[inv.id] = inv.cantidad_disponible;
      });
      setEditingInventario(editState);
    } catch (error) {
      toast.error("Error al actualizar inventario");
    }
  };

  const handleOpenConfig = (vendedor) => {
    setSelectedVendedor(vendedor);
    setVendedorConfig({
      apoyo_transporte: vendedor.apoyo_transporte || 0,
      tipo_auxilio_transporte: vendedor.tipo_auxilio_transporte || "periodico",
      otros_costos: vendedor.otros_costos || 0,
      notas_config: vendedor.notas_config || "",
      puede_registrar_gastos: vendedor.puede_registrar_gastos || false,
      can_deliver_orders: vendedor.can_deliver_orders || false,
    });
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    try {
      await api.put(`/usuarios/${selectedVendedor.id}/config`, vendedorConfig);
      toast.success("Configuración guardada");
      setConfigDialogOpen(false);
      fetchVendedores();
    } catch (error) {
      toast.error("Error al guardar configuración");
    }
  };

  // Funciones de Catálogo Digital
  const handleOpenCatalogDialog = (vendedor) => {
    setSelectedVendedor(vendedor);
    setCatalogCode(vendedor.catalog_code || "");
    setCatalogDialogOpen(true);
  };

  const handleGenerateCatalogCode = async () => {
    if (!selectedVendedor) return;
    setGeneratingCatalog(true);
    try {
      const response = await api.post(`/usuarios/${selectedVendedor.id}/catalog-code`);
      setCatalogCode(response.data.catalog_code);
      toast.success("Código de catálogo generado");
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar código");
    } finally {
      setGeneratingCatalog(false);
    }
  };

  const handleSaveCatalogCode = async () => {
    if (!selectedVendedor || !catalogCode.trim()) return;
    try {
      await api.put(`/usuarios/${selectedVendedor.id}/catalog-code`, {
        catalog_code: catalogCode.trim().toLowerCase()
      });
      toast.success("Código de catálogo actualizado");
      setCatalogDialogOpen(false);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar código");
    }
  };

  const copyCatalogUrl = () => {
    if (!catalogCode) return;
    const url = `${window.location.origin}/catalogo/${catalogCode}`;
    navigator.clipboard.writeText(url);
    setCatalogCopied(true);
    setTimeout(() => setCatalogCopied(false), 2000);
    toast.success("URL copiada");
  };

  const handleGenerateAllCatalogCodes = async () => {
    try {
      const response = await api.post("/usuarios/generate-all-catalog-codes");
      toast.success(response.data.message);
      fetchVendedores();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al generar códigos");
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      email: "",
      password: "",
      nombre: "",
      telefono: "",
      zona: "",
      rol: "vendedor",
      tipo_vendedor: "carrito",
      meta_diaria: "150000",
    });
  };

  const filtered = vendedores.filter(
    (v) =>
      v.nombre.toLowerCase().includes(search.toLowerCase()) ||
      v.email.toLowerCase().includes(search.toLowerCase()) ||
      v.zona?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vendedores">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Vendedores</h1>
          <p className="text-torta-muted">Gestiona tu equipo de ventas</p>
        </div>

        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-torta-brown hover:bg-torta-brown/90">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Vendedor
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingId ? "Editar Vendedor" : "Nuevo Vendedor"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Nombre completo</Label>
                <Input
                  required
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Nombre del vendedor"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="correo@email.com"
                />
              </div>
              {!editingId && (
                <div className="space-y-2">
                  <Label>Contraseña</Label>
                  <Input
                    type="password"
                    required={!editingId}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    placeholder="300 123 4567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Zona</Label>
                  <Input
                    value={form.zona}
                    onChange={(e) => setForm({ ...form, zona: e.target.value })}
                    placeholder="Chapinero"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Rol</Label>
                  <Select
                    value={form.rol}
                    onValueChange={(value) => setForm({ ...form, rol: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vendedor">Vendedor</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Meta diaria ($)</Label>
                  <Input
                    type="number"
                    value={form.meta_diaria}
                    onChange={(e) => setForm({ ...form, meta_diaria: e.target.value })}
                    placeholder="150000"
                  />
                </div>
              </div>
              {form.rol === "vendedor" && (
                <div className="space-y-2">
                  <Label>Tipo de Vendedor</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_vendedor: "carrito" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_vendedor === "carrito"
                          ? "border-torta-brown bg-torta-cream text-torta-dark"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <ShoppingCart className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Carrito</p>
                        <p className="text-xs opacity-70">Venta en calle</p>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_vendedor: "digital" })}
                      className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                        form.tipo_vendedor === "digital"
                          ? "border-blue-500 bg-blue-50 text-blue-800"
                          : "border-gray-200 text-gray-500 hover:border-gray-300"
                      }`}
                    >
                      <Smartphone className="w-5 h-5" />
                      <div className="text-left">
                        <p className="font-medium text-sm">Digital</p>
                        <p className="text-xs opacity-70">Venta online</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-torta-brown hover:bg-torta-brown/90">
                  {editingId ? "Guardar Cambios" : "Crear Vendedor"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-torta-muted" />
        <Input
          placeholder="Buscar por nombre, email o zona..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-torta-sand/50">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Vendedor</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Contacto</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Zona</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Rol/Tipo</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Config</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Estado</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-torta-sand/50">
              {filtered.map((v) => (
                <tr key={v.id} className="hover:bg-torta-cream/30">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {v.foto_url ? (
                        <img
                          src={v.foto_url}
                          alt={v.nombre}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-torta-cream rounded-full flex items-center justify-center text-torta-brown font-medium">
                          {v.nombre.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-torta-dark">{v.nombre}</p>
                        <p className="text-xs text-torta-muted">
                          Meta: ${v.meta_diaria?.toLocaleString("es-CO")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-torta-dark">{v.email}</p>
                    <p className="text-xs text-torta-muted">{v.telefono || "-"}</p>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-torta-dark">{v.zona || "Sin zona"}</span>
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          v.rol === "admin"
                            ? "bg-purple-100 text-purple-700"
                            : v.rol === "supervisor"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {v.rol}
                      </span>
                      {v.rol === "vendedor" && (
                        <div className="flex items-center gap-1 mt-1">
                          {v.tipo_vendedor === "digital" ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-600">
                              <Smartphone className="w-3 h-3" />
                              Digital
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-600">
                              <ShoppingCart className="w-3 h-3" />
                              Carrito
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    {v.rol === "vendedor" && (
                      <div className="text-xs text-torta-muted">
                        {v.apoyo_transporte > 0 && (
                          <p className="flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            ${v.apoyo_transporte?.toLocaleString("es-CO")}
                            <span className="text-[10px] opacity-70">
                              /{v.tipo_auxilio_transporte === "diario" ? "día" : "período"}
                            </span>
                          </p>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    <span
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                        v.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {v.activo ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-1">
                      {v.rol === "vendedor" && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenCatalogDialog(v)}
                            title="Catálogo Digital"
                            className={v.catalog_code ? "text-green-600" : "text-torta-muted"}
                          >
                            <Store className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleVerInventario(v)}
                            title="Ver inventario"
                          >
                            <Package className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenConfig(v)}
                            title="Configuración de comisiones"
                          >
                            <DollarSign className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedVendedor(v);
                          setPasswordDialogOpen(true);
                        }}
                        title="Cambiar contraseña"
                      >
                        <Key className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(v)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggle(v.id)}
                        className={v.activo ? "text-amber-600" : "text-green-600"}
                        title={v.activo ? "Desactivar" : "Activar"}
                      >
                        {v.activo ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedVendedor(v);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600"
                        title="Eliminar permanentemente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <p className="text-center py-8 text-torta-muted">No se encontraron vendedores</p>
        )}
      </div>

      {/* Inventario Dialog */}
      <Dialog open={inventarioDialogOpen} onOpenChange={setInventarioDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Package className="w-6 h-6 text-torta-brown" />
              Inventario de {selectedVendedor?.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {inventarioVendedor.length === 0 ? (
              <div className="text-center py-8">
                <Package className="w-12 h-12 text-torta-muted mx-auto mb-3" />
                <p className="text-torta-muted">Este vendedor no tiene inventario asignado</p>
              </div>
            ) : (
              inventarioVendedor.map((inv) => (
                <div
                  key={inv.id}
                  className={`flex items-center justify-between p-4 rounded-xl ${
                    inv.isNew 
                      ? "bg-blue-50 border border-blue-200" 
                      : "bg-torta-cream/50"
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-torta-dark">{inv.producto_nombre}</p>
                      {inv.isNew && (
                        <span className="text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded-full">
                          NUEVO
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-torta-muted">
                      {inv.isNew 
                        ? "Sin inventario asignado" 
                        : `Asignado: ${inv.cantidad_asignada} und`
                      }
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      <p className="text-xs text-torta-muted">
                        {inv.isNew ? "Asignar" : "Disponible"}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      className="w-20 text-center"
                      value={editingInventario[inv.id] ?? inv.cantidad_disponible}
                      onChange={(e) =>
                        setEditingInventario({
                          ...editingInventario,
                          [inv.id]: e.target.value,
                        })
                      }
                    />
                    <Button
                      size="sm"
                      onClick={() => handleUpdateInventario(inv)}
                      className={inv.isNew 
                        ? "bg-blue-500 hover:bg-blue-600" 
                        : "bg-torta-brown hover:bg-torta-brown/90"
                      }
                    >
                      {inv.isNew ? <Plus className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Key className="w-6 h-6 text-torta-brown" />
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-torta-muted">
              Cambiando contraseña para: <strong>{selectedVendedor?.nombre}</strong>
            </p>
            <p className="text-sm text-torta-muted">Email: {selectedVendedor?.email}</p>
            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nueva contraseña"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-torta-muted hover:text-torta-dark"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setNewPassword("");
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={!newPassword}
                className="flex-1 bg-torta-brown hover:bg-torta-brown/90"
              >
                Cambiar Contraseña
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2 text-red-600">
              <Trash2 className="w-6 h-6" />
              Eliminar Permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-800 font-medium">¿Estás seguro?</p>
              <p className="text-red-600 text-sm mt-1">
                Esta acción eliminará permanentemente a <strong>{selectedVendedor?.nombre}</strong> y
                todos sus datos asociados. Esta acción no se puede deshacer.
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700">
                Eliminar Permanentemente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Config Dialog (Comisiones individuales) */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-torta-brown" />
              Configuración de {selectedVendedor?.nombre}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-torta-brown" />
                Apoyo Transporte
              </Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  value={vendedorConfig.apoyo_transporte}
                  onChange={(e) =>
                    setVendedorConfig({ ...vendedorConfig, apoyo_transporte: parseInt(e.target.value) || 0 })
                  }
                  placeholder="0"
                  className="flex-1"
                />
                <select
                  value={vendedorConfig.tipo_auxilio_transporte}
                  onChange={(e) =>
                    setVendedorConfig({ ...vendedorConfig, tipo_auxilio_transporte: e.target.value })
                  }
                  className="px-3 py-2 border rounded-md text-sm bg-white"
                >
                  <option value="periodico">Por Período</option>
                  <option value="diario">Por Día Trabajado</option>
                </select>
              </div>
              <p className="text-xs text-torta-muted">
                {vendedorConfig.tipo_auxilio_transporte === "diario" 
                  ? "Se multiplica por los días con ventas reportadas" 
                  : "Monto fijo por período de liquidación"
                }
              </p>
            </div>
            <div className="space-y-2">
              <Label>Otros Costos ($/día)</Label>
              <Input
                type="number"
                min="0"
                value={vendedorConfig.otros_costos}
                onChange={(e) =>
                  setVendedorConfig({ ...vendedorConfig, otros_costos: parseInt(e.target.value) || 0 })
                }
                placeholder="0"
              />
              <p className="text-xs text-torta-muted">Otros costos asociados al vendedor</p>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Input
                value={vendedorConfig.notas_config}
                onChange={(e) => setVendedorConfig({ ...vendedorConfig, notas_config: e.target.value })}
                placeholder="Notas sobre la configuración..."
              />
            </div>
            
            {/* Permiso para registrar gastos */}
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Receipt className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-amber-800">Puede Registrar Gastos</Label>
                    <p className="text-xs text-amber-600">Habilita módulo de registro de compras e insumos</p>
                  </div>
                </div>
                <Switch
                  checked={vendedorConfig.puede_registrar_gastos}
                  onCheckedChange={(checked) => 
                    setVendedorConfig({ ...vendedorConfig, puede_registrar_gastos: checked })
                  }
                />
              </div>

              {/* Toggle para permitir entregas */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Truck className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-blue-800">Puede Hacer Entregas</Label>
                    <p className="text-xs text-blue-600">Permite asignarle pedidos para entregar como domiciliario</p>
                  </div>
                </div>
                <Switch
                  checked={vendedorConfig.can_deliver_orders}
                  onCheckedChange={(checked) => 
                    setVendedorConfig({ ...vendedorConfig, can_deliver_orders: checked })
                  }
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setConfigDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleSaveConfig} className="flex-1 bg-torta-brown hover:bg-torta-brown/90">
                Guardar Configuración
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Catálogo Digital */}
      <Dialog open={catalogDialogOpen} onOpenChange={setCatalogDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-torta-brown" />
              Catálogo Digital
            </DialogTitle>
          </DialogHeader>
          {selectedVendedor && (
            <div className="space-y-4">
              <div className="bg-torta-cream/50 rounded-xl p-3">
                <p className="font-medium text-torta-dark">{selectedVendedor.nombre}</p>
                <p className="text-sm text-torta-muted">{selectedVendedor.email}</p>
              </div>

              {catalogCode ? (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Código de Catálogo</Label>
                    <div className="flex gap-2">
                      <Input
                        value={catalogCode}
                        onChange={(e) => setCatalogCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        placeholder="mi-catalogo"
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        onClick={handleSaveCatalogCode}
                        size="sm"
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-torta-muted">Solo letras minúsculas, números y guiones</p>
                  </div>

                  <div className="space-y-2">
                    <Label>URL del Catálogo</Label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm bg-torta-cream px-3 py-2 rounded-lg truncate">
                        {window.location.origin}/catalogo/{catalogCode}
                      </code>
                      <Button
                        variant="outline"
                        onClick={copyCatalogUrl}
                        size="sm"
                      >
                        {catalogCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => window.open(`/catalogo/${catalogCode}`, "_blank")}
                        size="sm"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 bg-torta-cream/30 rounded-xl">
                  <Store className="w-12 h-12 text-torta-muted mx-auto mb-3" />
                  <p className="text-torta-muted mb-4">Este vendedor no tiene catálogo digital</p>
                  <Button
                    onClick={handleGenerateCatalogCode}
                    disabled={generatingCatalog}
                    className="bg-torta-brown hover:bg-torta-brown/90"
                  >
                    {generatingCatalog ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Generar Código de Catálogo
                      </>
                    )}
                  </Button>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setCatalogDialogOpen(false)} className="flex-1">
                  Cerrar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
